import React from 'react';
import PropTypes from 'prop-types';
import { localPoint } from '@visx/event';
import Pie from '@visx/shape/lib/shapes/Pie';
import { Group } from '@visx/group';
import { useTheme } from '@material-ui/core';
import { withTooltip, TooltipWithBounds } from '@visx/tooltip';
import _ from 'lodash';
import {
  usePortfolioStateContext,
  usePortfolioDispatchContext,
} from 'Components/UI/Portfolio/PortfolioContext';
import { queryBySecurityType } from 'Components/UI/Portfolio/PortfolioActions';
import { currency } from 'Helpers/Formats';
import AnimatedPie from './AnimatedPie';

// accessor function
const getValue = d => parseFloat(d.resource.attr('value'));

const getTooltip = resource => {
  if (!resource) {
    return null;
  }
  return `
${resource.attr('label')} - ${currency(
  parseFloat(resource.attr('value')),
  false,
)}
  
Percentage of Portfolio - ${resource.attr('percentageValue')}`;
};

function DonutChart({
  width,
  height,
  margin,
  animate,
  showTooltip,
  hideTooltip,
  tooltipLeft,
  tooltipTop,
  tooltipOpen,
  tooltipData,
  holdings,
}) {
  const dispatch = usePortfolioDispatchContext();
  const state = usePortfolioStateContext();
  const { loading } = state;
  const theme = useTheme();

  const handleMouseOver = React.useCallback(
    (event) => {
      const coords = localPoint(event.target.ownerSVGElement, event);
      const { tooltip } = event.target.dataset;

      showTooltip({
        tooltipLeft: coords.x,
        tooltipTop: coords.y,
        tooltipData: tooltip,
      });
    },
    [showTooltip],
  );

  React.useEffect(() => {
    if (tooltipOpen) hideTooltip();
  }, [loading]);

  if (width < 10 || !holdings) return null;

  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  const radius = Math.min(innerWidth, innerHeight) / 2;
  const centerY = innerHeight / 2;
  const centerX = innerWidth / 2;
  const donutThickness = 50;

  const assetClassChartData = _(holdings.attr('assetClassChartData'))
    .toArray()
    .sortBy(a => a.id())
    .map(r => ({
      resource: r,
      tooltip: getTooltip(r),
    }))
    .value();

  const categoryChartData = _(holdings.attr('categoryChartData'))
    .toArray()
    .sortBy(a => [a.attr('type'), a.attr('label')])
    .map(r => ({
      resource: r,
      tooltip: getTooltip(r),
    }))
    .value();

  return (
    <>
      {tooltipOpen && (
        <TooltipWithBounds
          // set this to random so it correctly updates with parent bounds
          key={Math.random()}
          top={tooltipTop}
          left={tooltipLeft}
        >
          <p
            style={{
              whiteSpace: 'pre',
              textAlign: 'left',
            }}
          >
            {tooltipData}
          </p>
        </TooltipWithBounds>
      )}
      <svg width={width} height={height}>
        <Group top={centerY + margin.top} left={centerX + margin.left}>
          <Pie
            data={assetClassChartData}
            pieValue={getValue}
            outerRadius={radius - donutThickness * 1.3}
            pieSort={null}
            pieSortValues={null}
          >
            {pie => (
              <AnimatedPie
                {...pie}
                showLabel
                showTooltip={handleMouseOver}
                hideTooltip={() => hideTooltip()}
                animate={animate}
                getKey={({ data: { resource } }) => resource.attr('label')}
                onClickDatum={({ data: { resource } }) => animate
                  && queryBySecurityType(resource.attr('label'), dispatch, state)
                }
                getColor={({ data: { resource } }) => resource.attr('color')}
              />
            )}
          </Pie>
          <Pie
            data={categoryChartData}
            pieSort={null}
            pieSortValues={null}
            pieValue={getValue}
            outerRadius={radius}
            innerRadius={radius - donutThickness}
          >
            {pie => (
              <AnimatedPie
                {...pie}
                showLabel
                showTooltip={handleMouseOver}
                hideTooltip={() => hideTooltip()}
                animate={animate}
                getKey={({ data: { resource } }) => resource.attr('label')}
                onClickDatum={({ data: { resource } }) => animate
                  && queryBySecurityType(resource.attr('type'), dispatch, state)
                }
                getColor={({ data: { resource } }) => resource.attr('color')}
              />
            )}
          </Pie>
        </Group>
        {animate && (
          <text
            textAnchor="end"
            x={width - 16}
            y={height - 16}
            fill={theme.palette.primary.main}
            fontSize={11}
            fontWeight={300}
            pointerEvents="none"
          >
            Click segments to update
          </text>
        )}
      </svg>
    </>
  );
}

export default withTooltip(DonutChart);

DonutChart.defaultProps = {
  margin: {
    top: 20,
    right: 20,
    bottom: 20,
    left: 20,
  },
  animate: true,
};

DonutChart.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.object.isRequired,
  margin: PropTypes.number,
  animate: PropTypes.bool,
  holdings: PropTypes.object.isRequired,
  tooltipData: PropTypes.node.isRequired,
  tooltipLeft: PropTypes.number.isRequired,
  tooltipTop: PropTypes.number.isRequired,
  tooltipOpen: PropTypes.bool.isRequired,
  hideTooltip: PropTypes.func.isRequired,
};
